import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { Box, useTheme } from "@mui/material";
import type { DateTime } from "luxon";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { FONT_SIZE, INK, NEUTRAL, SHIFT_HISTORY_HEADER } from "../../theme/tokens";
import type { NurseInShift } from "../../utils/models";
import MetaChip from "../UIToolkit/Badges/MetaChip";
import { EyebrowLabel } from "../UIToolkit/molecules/EyebrowLabel";
import SystemButton from "../UIToolkit/SystemButton/SystemButton";
import { SHIFT_HISTORY_TEST_ID } from "./shiftHistoryDisplay";

type Props = {
	nurseInShift: NurseInShift;
	nurseName: string;
	onClose: () => void;
	backToSchedule?: () => void;
};

const TIME_FORMAT = "h:mm a";

/** The shift being read stays visible while its history scrolls. */
export const ShiftHistoryHeader: FC<Props> = ({ nurseInShift, nurseName, onClose, backToSchedule }) => {
	const { t, i18n } = useTranslation();
	const theme = useTheme();
	const start = nurseInShift.startTime.setLocale(i18n.language);
	const end = nurseInShift.endTime.setLocale(i18n.language);
	const dayLabel = (date: DateTime): string => date.toLocaleString({ weekday: "short", month: "short", day: "numeric", year: "numeric" });
	const dateLabel = start.hasSame(end, "day") ? dayLabel(start) : `${dayLabel(start)} – ${dayLabel(end)}`;
	return (
		<Box
			component="header"
			sx={{
				position: "sticky",
				top: 0,
				zIndex: theme.zIndex.appBar,
				px: { xs: 2, sm: 3 },
				pt: 2,
				pb: 2.5,
				bgcolor: "background.paper",
				borderBottom: `1px solid ${NEUTRAL[200]}`,
			}}
		>
			<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
				{backToSchedule === undefined ? (
					<EyebrowLabel>{t("shiftHistory.eyebrow")}</EyebrowLabel>
				) : (
					<SystemButton
						buttonVariant="borderless"
						size="small"
						noFullWidthMobile
						leadingIcon={<ArrowBackRoundedIcon sx={{ transform: theme.direction === "rtl" ? "rotate(180deg)" : "none" }} />}
						onClick={backToSchedule}
						data-testid={`${SHIFT_HISTORY_TEST_ID}-back-to-schedule`}
					>
						{t("shiftHistory.backToSchedule")}
					</SystemButton>
				)}
				<SystemButton
					buttonVariant="borderless"
					size="compact"
					iconButton
					noFullWidthMobile
					onClick={onClose}
					aria-label={t("shiftHistory.close")}
					data-testid={`${SHIFT_HISTORY_TEST_ID}-close`}
				>
					<CloseRoundedIcon fontSize="small" />
				</SystemButton>
			</Box>
			{backToSchedule !== undefined && <EyebrowLabel sx={{ mb: 1.25 }}>{t("shiftHistory.eyebrow")}</EyebrowLabel>}
			<Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.75 }}>
				<Box
					aria-hidden
					sx={{
						flexShrink: 0,
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						justifyContent: "center",
						width: SHIFT_HISTORY_HEADER.DATE_TILE_SIZE_PX,
						height: SHIFT_HISTORY_HEADER.DATE_TILE_SIZE_PX,
						borderRadius: 1,
						bgcolor: "primary.main",
						color: "primary.contrastText",
						lineHeight: 1.1,
						fontWeight: 700,
						fontSize: FONT_SIZE.CAPTION,
					}}
				>
					<span>{start.toFormat("ccc").toUpperCase()}</span>
					<span>{start.toFormat("dd")}</span>
				</Box>
				<Box sx={{ minWidth: 0 }}>
					<Box
						component="h2"
						data-testid={`${SHIFT_HISTORY_TEST_ID}-heading`}
						sx={{
							m: 0,
							fontSize: FONT_SIZE.TITLE_XXL,
							fontWeight: 700,
							color: INK.PRIMARY,
							lineHeight: 1.2,
							direction: "ltr",
							textAlign: theme.direction === "rtl" ? "right" : "left",
						}}
					>
						{`${start.toFormat(TIME_FORMAT)} – ${end.toFormat(TIME_FORMAT)}`}
					</Box>
					<Box component="p" sx={{ m: 0, mt: 0.25, color: INK.SECONDARY, fontSize: FONT_SIZE.SECONDARY }}>
						<Box component="bdi">{nurseName}</Box> · <Box component="bdi">{dateLabel}</Box>
					</Box>
					{(nurseInShift.location !== undefined || nurseInShift.jobName !== undefined) && (
						<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1.25 }}>
							{nurseInShift.location !== undefined && <MetaChip label={nurseInShift.location} />}
							{nurseInShift.jobName !== undefined && <MetaChip label={nurseInShift.jobName} />}
						</Box>
					)}
				</Box>
			</Box>
		</Box>
	);
};
